import React from 'react';
import { t } from '@lingui/core/macro';

import {
  SERVICE_TYPE_CHOICES,
  ServiceType,
} from '@rover/constants/src/translations/service.constants';
import { SERVICE_SELECTION_OPTIONS } from '@rover/constants/src/translations/serviceSelector.constants';
import { Box, Flex, Text } from '@rover/kibble/core';
import ServiceIcon from '@rover/shared/js/search/components/ServiceIcon';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { PetType } from '@rover/types/src/Pet';

import SelectableOption from './SelectableOption';

type Props = {
  selectedService: ServiceType | undefined;
  onSelect: (serviceType: ServiceType) => void;
  petType: PetType[] | undefined;
};

const CAT_SLUGS: ServiceType[] = [
  SERVICE_TYPE_CHOICES.boarding,
  SERVICE_TYPE_CHOICES.traveling,
  SERVICE_TYPE_CHOICES.dropIn,
];

const DOG_ONLY_SLUGS: ServiceType[] = [SERVICE_TYPE_CHOICES.dayCare, SERVICE_TYPE_CHOICES.walking];

const DOG_SLUGS: ServiceType[] = [...CAT_SLUGS, ...DOG_ONLY_SLUGS];

export const getIsDogOnlyService = (serviceType: ServiceType | undefined): boolean =>
  !!serviceType && DOG_ONLY_SLUGS.includes(serviceType);

const getServiceOptions = (
  petType: PetType[] | undefined
): Array<{ slug: ServiceType; label: string }> => {
  const includedSlugs = getIsCatOnlyPetType(petType ?? []) ? CAT_SLUGS : DOG_SLUGS;
  return SERVICE_SELECTION_OPTIONS()
    .filter(({ value }) => includedSlugs.includes(value))
    .map(({ value, title }) => ({ slug: value, label: title }));
};

const ServiceTypeGrid = ({ selectedService, onSelect, petType }: Props): JSX.Element => {
  return (
    <Box>
      <Text size="200" fontWeight="semibold" mb="2x" display="block">
        {t`Who needs`}
      </Text>
      <Flex
        webAs="fieldset"
        role="radiogroup"
        flexWrap="wrap"
        gap="2x"
        sx={{ border: 'none', padding: 0, margin: 0 }}
      >
        {getServiceOptions(petType).map(({ slug, label }) => (
          <Box key={slug} width="calc(50% - 4px)">
            <SelectableOption
              isSelected={selectedService === slug}
              onPress={() => onSelect(slug)}
              label={label}
              icon={<ServiceIcon serviceType={slug} />}
            />
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

export default ServiceTypeGrid;
